import {
  ContractorType,
  LegalEntityList,
  PersonList
} from '@insurance/api/requests'
import { AddPersonButton } from '@insurance/components'
import { Text } from '@shared/components'
import { getAge } from '@shared/utils'
import { Checkbox, Flex, Form, Row } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import { Add } from 'iconsax-react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { ContractorLegalPerson } from './ContractorLegalPerson'
import { ContractorLocation } from './ContractorLocation'
import { ContractorPerson } from './ContractorPerson'
import { ContractorTypeSelect } from './ContractorTypeSelect'

type Props = {
  minAge?: number
  minExperience?: number
  isEditable?: boolean
  contractorKey?: string
  personsKey?: string
  sameContractorKey?: string
  resident?: boolean
  showSameContractorField?: boolean
  showBirthDate?: boolean
  showExperience?: boolean
  showResident?: boolean
  disableContractorType?: boolean
  disableBirthDate?: boolean
  disableExperience?: boolean

  onChangeBirthDate?: (value?: Dayjs) => void
  onChangeExperience?: (value?: number | null) => void
}

export const ContractorTypeFields = ({
  minAge,
  minExperience,
  isEditable = false,
  contractorKey = 'contractor',
  personsKey = 'persons',
  sameContractorKey = 'same_contractor_as_first_person',
  resident,
  showSameContractorField = true,
  showBirthDate,
  showExperience,
  showResident,
  disableContractorType,
  disableBirthDate,
  disableExperience,

  onChangeBirthDate,
  onChangeExperience
}: Props) => {
  const { t } = useTranslation()
  const formInstance = Form.useFormInstance()
  const contractor = Form.useWatch(contractorKey, { preserve: true })
  const sameContractorAsFirstPerson = Form.useWatch(sameContractorKey, {
    preserve: true
  })
  const firstPersonBirthDay = Form.useWatch([personsKey, 0, 'date_of_birth'], {
    preserve: true
  })
  const firstPersonResident = Form.useWatch([personsKey, 0, 'resident'], {
    preserve: true
  })
  const isFieldResident = Form.useWatch([contractorKey, 'person', 'resident'], {
    preserve: true
  })

  useEffect(() => {
    if (minAge && firstPersonBirthDay && getAge(firstPersonBirthDay) < minAge)
      formInstance.setFieldValue(sameContractorKey, false)
  }, [firstPersonBirthDay, formInstance, minAge, sameContractorKey])

  const contractorType = contractor?.content_type
  const personValue =
    contractorType === ContractorType.PERSON
      ? contractor?.person
      : contractor?.legal_entity

  const handleAddContractor = (
    person: (PersonList | LegalEntityList) & { resident?: boolean }
  ) => {
    if ('date_of_birth' in person) {
      const dateOfBirth = person.date_of_birth
        ? dayjs(person.date_of_birth)
        : undefined
      person.date_of_birth = dateOfBirth as unknown as string
      onChangeBirthDate?.(dateOfBirth)
    }

    if (!showResident) person.resident = !!resident
    if (typeof person.resident !== 'boolean') person.resident = true

    if (contractorType === ContractorType.PERSON)
      formInstance.setFieldValue([contractorKey, 'person'], person)

    if (contractorType === ContractorType.LEGALENTITY)
      formInstance.setFieldValue([contractorKey, 'legal_entity'], person)
  }

  const isResident = sameContractorAsFirstPerson
    ? firstPersonResident || resident
    : isFieldResident

  return (
    <Flex vertical gap={12}>
      <Flex
        align="center"
        gap={12}
        justify="space-between"
        style={{ paddingBottom: 12 }}
      >
        <Text size={16} strong>
          {t('contractor')}
        </Text>

        <AddPersonButton
          disabled={isEditable}
          personType={contractorType}
          type="link"
          icon={<Add size={16} />}
          value={personValue}
          onAdd={handleAddContractor}
        >
          {t('add_existing_contractor')}
        </AddPersonButton>
      </Flex>

      <div>
        <ContractorTypeSelect
          disableContractorType={disableContractorType}
          isEditable={isEditable}
          contractorKey={contractorKey}
        />

        {contractorType === ContractorType.PERSON && (
          <>
            {(!firstPersonBirthDay ||
              !minAge ||
              getAge(firstPersonBirthDay) >= minAge) &&
              showSameContractorField && (
                <Form.Item name={sameContractorKey} valuePropName="checked">
                  <Checkbox disabled={isEditable}>
                    {t('use_same_contractor_as_first_insured_person')}
                  </Checkbox>
                </Form.Item>
              )}

            {!sameContractorAsFirstPerson && (
              <ContractorPerson
                minAge={minAge}
                minExperience={minExperience}
                isEditable={isEditable}
                contractorKey={contractorKey}
                personsKey={personsKey}
                showBirthDate={showBirthDate}
                showExperience={showExperience}
                showResident={showResident}
                disableBirthDate={disableBirthDate}
                disableExperience={disableExperience}
                onChangeBirthDate={onChangeBirthDate}
                onChangeExperience={onChangeExperience}
              />
            )}

            <Row gutter={12} align="bottom">
              {isResident && (
                <ContractorLocation
                  disabled={isEditable}
                  locationKey={[contractorKey, 'person']}
                />
              )}
            </Row>
          </>
        )}

        {contractorType === ContractorType.LEGALENTITY && (
          <ContractorLegalPerson
            isEditable={isEditable}
            contractorKey={contractorKey}
            resident={resident}
          >
            <ContractorLocation
              disabled={isEditable}
              locationKey={[contractorKey, 'legal_entity']}
            />
          </ContractorLegalPerson>
        )}
      </div>
    </Flex>
  )
}
